import React from 'react';
import { Play, Pause, RotateCcw, PlaySquare } from 'lucide-react';

export type PlaybackMode = 'mixed' | 'original';

interface StudioTransportBarProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  hasTakes: boolean;
  playbackMode: PlaybackMode;
  onTogglePlay: () => void;
  onPlayRange: (paddingSec?: number) => void;
  onSeek: (seconds: number) => void;
  onSetPlaybackMode: (mode: PlaybackMode) => void;
  formatTime: (secs: number) => string;
}

export const StudioTransportBar: React.FC<StudioTransportBarProps> = ({
  isPlaying,
  currentTime,
  duration,
  hasTakes,
  playbackMode,
  onTogglePlay,
  onPlayRange,
  onSeek,
  onSetPlaybackMode,
  formatTime
}) => {
  return (
    <div className="studio-transport">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          type="button"
          onClick={onTogglePlay}
          style={{
            padding: '6px',
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.1)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            border: 'none'
          }}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} fill="currentColor" />}
        </button>

        <button
          type="button"
          onClick={() => onPlayRange(2.0)}
          style={{
            padding: '6px',
            borderRadius: '8px',
            background: 'rgba(139, 92, 246, 0.22)',
            border: '1px solid rgba(139, 92, 246, 0.45)',
            color: '#c4b5fd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
          title={hasTakes ? 'Play Range: latest take ±2s padding' : 'Play Range: selected line ±2s padding'}
        >
          <PlaySquare size={16} />
        </button>

        <button
          type="button"
          onClick={() => onSeek(0)}
          style={{
            padding: '6px',
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.1)',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            border: 'none'
          }}
          title="Rewind to start"
        >
          <RotateCcw size={16} />
        </button>

        <span style={{ fontFamily: 'var(--font-mono)', color: '#e2e8f0', fontSize: '0.75rem', marginLeft: '6px' }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      {/* Audio Track Mode Switcher - Aligned to bottom-right */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: 'rgba(0,0,0,0.7)',
          padding: '3px 4px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border-glass)',
          fontSize: '0.75rem',
          marginLeft: 'auto'
        }}
      >
        <button
          type="button"
          onClick={() => onSetPlaybackMode('mixed')}
          style={{
            padding: '4px 10px',
            borderRadius: '4px',
            fontWeight: 700,
            border: 'none',
            cursor: 'pointer',
            background: playbackMode === 'mixed' ? 'var(--accent-purple)' : 'transparent',
            color: playbackMode === 'mixed' ? '#fff' : 'var(--text-muted)',
            transition: 'all 0.15s ease'
          }}
          title="SoulSync Mix: original vocals play on non-armed lines, muted for armed lines and active replacement takes"
        >
          SoulSync Mix
        </button>
        <button
          type="button"
          onClick={() => onSetPlaybackMode('original')}
          style={{
            padding: '4px 10px',
            borderRadius: '4px',
            fontWeight: 700,
            border: 'none',
            cursor: 'pointer',
            background: playbackMode === 'original' ? 'var(--accent-purple)' : 'transparent',
            color: playbackMode === 'original' ? '#fff' : 'var(--text-muted)',
            transition: 'all 0.15s ease'
          }}
          title="Original Vocals: original voice stem plays continuously, muted only when a line is actively armed"
        >
          Original Vocals
        </button>
      </div>
    </div>
  );
};
